import React, { useState, useEffect, useMemo } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Target, DollarSign, Clock, Users, CheckCircle2, Calendar, AlertCircle } from 'lucide-react';
import { Card } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const COLORS = ['#1C3E53', '#4A90A4', '#2D7A4C', '#C07F00', '#B83A2A', '#6BA5B8', '#579BFC'];

const AnalyticsDashboard = ({ boardType, tasks, boardName }) => {
  const [timeRange, setTimeRange] = useState('30'); // 7, 30, 90, all

  // Filter tasks by time range
  const filteredTasks = useMemo(() => {
    if (timeRange === 'all') return tasks;
    
    const daysAgo = parseInt(timeRange);
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysAgo);
    
    return tasks.filter(task => {
      const taskDate = task.createdAt ? new Date(task.createdAt) : null;
      return taskDate && taskDate >= cutoffDate;
    });
  }, [tasks, timeRange]);

  // Project Board Analytics
  const renderProjectAnalytics = () => {
    const totalTasks = filteredTasks.length;
    const completedTasks = filteredTasks.filter(t =>
      t.status?.value === 'done' || t.status?.label?.toLowerCase() === 'done'
    ).length;
    const avgProgress = totalTasks > 0 ? filteredTasks.reduce((sum, t) => sum + (t.progress || 0), 0) / totalTasks : 0;

    // Tasks by status
    const statusData = filteredTasks.reduce((acc, task) => {
      const status = task.status?.label || task.status?.value || 'Unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});
    
    const statusChartData = Object.entries(statusData).map(([status, count]) => ({
      name: status,
      value: count
    }));

    // Burndown chart - tasks completed over time
    const completedByDate = filteredTasks
      .filter(t => t.status?.value === 'done' || t.status?.label?.toLowerCase() === 'done')
      .reduce((acc, task) => {
        const date = task.updatedAt ? new Date(task.updatedAt).toLocaleDateString('fr-FR') : 'Unknown';
        acc[date] = (acc[date] || 0) + 1;
        return acc;
      }, {});
    
    const burndownData = Object.entries(completedByDate).map(([date, count]) => ({
      date,
      completed: count
    }));

    // Progress distribution
    const progressRanges = {
      '0-25%': 0,
      '26-50%': 0,
      '51-75%': 0,
      '76-100%': 0
    };
    
    filteredTasks.forEach(task => {
      const progress = task.progress || 0;
      if (progress <= 25) progressRanges['0-25%']++;
      else if (progress <= 50) progressRanges['26-50%']++;
      else if (progress <= 75) progressRanges['51-75%']++;
      else progressRanges['76-100%']++;
    });
    
    const progressData = Object.entries(progressRanges).map(([range, count]) => ({
      range,
      count
    }));

    return (
      <div className="space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Tasks</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{totalTasks}</p>
                <p className="text-xs text-green-600 mt-1">{completedTasks} completed</p>
              </div>
              <Target className="h-10 w-10 text-[#1C3E53]" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Completion Rate</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0}%
                </p>
                <p className="text-xs text-gray-500 mt-1">of all tasks</p>
              </div>
              <CheckCircle2 className="h-10 w-10 text-green-600" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg Progress</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{Math.round(avgProgress)}%</p>
                <p className="text-xs text-gray-500 mt-1">across tasks</p>
              </div>
              <TrendingUp className="h-10 w-10 text-purple-600" />
            </div>
          </Card>
        </div>

        {/* Charts Row 1 - Only show if data exists */}
        {(statusChartData.length > 0 || totalTasks > 0) && (
          <div className="grid grid-cols-2 gap-4">
            {statusChartData.length > 0 && (
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Tasks by Status</h3>
                <div style={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={statusChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({name, percent}) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        outerRadius={90}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {statusChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            )}

            {totalTasks > 0 && (
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Progress Distribution</h3>
                <div style={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                    <BarChart data={progressData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="range" tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#1C3E53" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Charts Row 2 - Only show if data exists */}
        {burndownData.length > 0 && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Tasks Completed Over Time</h3>
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer>
                <LineChart data={burndownData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="completed" stroke="#2D7A4C" strokeWidth={2} name="Completed" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        )}
        
        {/* Contributions par Personne */}
        {filteredTasks.length > 0 && (() => {
          const contributionData = filteredTasks.reduce((acc, task) => {
            const assignees = task.assignees || (task.person ? [task.person] : []);
            assignees.forEach(assignee => {
              const name = assignee.name || 'Unassigned';
              if (!acc[name]) acc[name] = { total: 0, completed: 0 };
              acc[name].total++;
              if (task.status?.value === 'done' || task.status?.label?.toLowerCase() === 'done') {
                acc[name].completed++;
              }
            });
            return acc;
          }, {});
          
          const contributionChartData = Object.entries(contributionData).map(([name, data]) => ({
            name,
            total: data.total,
            completed: data.completed,
            percentage: data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0
          })).sort((a, b) => b.total - a.total);
          
          return contributionChartData.length > 0 ? (
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Team Contributions</h3>
              <div style={{ width: '100%', height: 300 }}>
                <ResponsiveContainer>
                  <BarChart data={contributionChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="total" fill="#1C3E53" name="Total Tasks" />
                    <Bar dataKey="completed" fill="#2D7A4C" name="Completed" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>
          ) : null;
        })()}
        
        {/* Empty state when no charts */}
        {statusChartData.length === 0 && totalTasks === 0 && burndownData.length === 0 && (
          <Card className="p-12 text-center">
            <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Data Yet</h3>
            <p className="text-gray-600">Create some tasks to see analytics and insights</p>
          </Card>
        )}
      </div>
    );
  };

  // Marketing & Sales analytics similar structure...
  const renderMarketingAnalytics = () => {
    const totalCampaigns = filteredTasks.length;
    const activeCampaigns = filteredTasks.filter(t =>
      ['in_production', 'published', 'live'].includes(t.status?.value) ||
      ['in production', 'published', 'live'].includes(t.status?.label?.toLowerCase())
    ).length;
    const publishedCampaigns = filteredTasks.filter(t =>
      t.status?.value === 'published' || t.status?.label?.toLowerCase() === 'published'
    ).length;

    // Channel performance
    const channelData = filteredTasks.reduce((acc, task) => {
      const channel = task.channel || 'Other';
      if (!acc[channel]) acc[channel] = { count: 0, totalPerformance: 0 };
      acc[channel].count++;
      acc[channel].totalPerformance += task.performance || 0;
      return acc;
    }, {});

    const channelChartData = Object.entries(channelData).map(([channel, data]) => ({
      channel,
      campaigns: data.count,
      avgPerformance: data.count > 0 ? Math.round(data.totalPerformance / data.count) : 0
    }));

    const statusData = filteredTasks.reduce((acc, task) => {
      const status = task.status?.label || task.status?.value || 'Unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    const statusChartData = Object.entries(statusData).map(([status, count]) => ({
      name: status,
      value: count
    }));

    return (
      <div className="space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Campaigns</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{totalCampaigns}</p>
                <p className="text-xs text-orange-600 mt-1">{activeCampaigns} active</p>
              </div>
              <TrendingUp className="h-10 w-10 text-[#1C3E53]" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Published</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{publishedCampaigns}</p>
                <p className="text-xs text-gray-500 mt-1">live campaigns</p>
              </div>
              <CheckCircle2 className="h-10 w-10 text-green-600" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Top Channel</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {channelChartData[0]?.channel || 'N/A'}
                </p>
                <p className="text-xs text-gray-500 mt-1">{channelChartData[0]?.campaigns || 0} campaigns</p>
              </div>
              <Users className="h-10 w-10 text-blue-600" />
            </div>
          </Card>
        </div>

        {/* Charts - Only if data */}
        {(channelChartData.length > 0 || statusChartData.length > 0) && (
          <div className="grid grid-cols-2 gap-4">
            {channelChartData.length > 0 && (
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Performance by Channel</h3>
                <div style={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                    <BarChart data={channelChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="channel" tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="campaigns" fill="#1C3E53" name="Campaigns" />
                      <Bar dataKey="avgPerformance" fill="#4A90A4" name="Avg Performance" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            )}

            {statusChartData.length > 0 && (
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Campaigns by Status</h3>
                <div style={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={statusChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({name, percent}) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        outerRadius={90}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {statusChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            )}
          </div>
        )}
        
        {/* Empty state */}
        {totalCampaigns === 0 && (
          <Card className="p-12 text-center">
            <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Campaign Data</h3>
            <p className="text-gray-600">Create marketing campaigns to see analytics</p>
          </Card>
        )}
      </div>
    );
  };

  // Sales Board Analytics
  const renderSalesAnalytics = () => {
    const totalValue = filteredTasks.reduce((sum, t) => sum + (t.deal_value || 0), 0);
    const wonDeals = filteredTasks.filter(t => 
      t.status?.value === 'won' || t.status?.value === 'closed_won' ||
      t.status?.label?.toLowerCase().includes('won')
    );
    const wonValue = wonDeals.reduce((sum, t) => sum + (t.deal_value || 0), 0);
    const avgDealValue = filteredTasks.length > 0 ? totalValue / filteredTasks.length : 0;
    const conversionRate = filteredTasks.length > 0 ? (wonDeals.length / filteredTasks.length) * 100 : 0;

    // Deals by stage
    const stageData = filteredTasks.reduce((acc, task) => {
      const stage = task.status?.label || task.status?.value || 'Unknown';
      if (!acc[stage]) acc[stage] = { count: 0, value: 0 };
      acc[stage].count++;
      acc[stage].value += task.deal_value || 0;
      return acc;
    }, {});

    const stageChartData = Object.entries(stageData).map(([stage, data]) => ({
      stage,
      deals: data.count,
      value: Math.round(data.value)
    }));

    return (
      <div className="space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Pipeline</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  €{(totalValue / 1000).toFixed(0)}k
                </p>
                <p className="text-xs text-gray-500 mt-1">{filteredTasks.length} deals</p>
              </div>
              <DollarSign className="h-10 w-10 text-[#1C3E53]" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Won Deals</p>
                <p className="text-2xl font-bold text-green-600 mt-1">
                  €{(wonValue / 1000).toFixed(0)}k
                </p>
                <p className="text-xs text-gray-500 mt-1">{wonDeals.length} closed</p>
              </div>
              <CheckCircle2 className="h-10 w-10 text-green-600" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Conversion</p>
                <p className="text-2xl font-bold text-purple-600 mt-1">
                  {conversionRate.toFixed(1)}%
                </p>
                <p className="text-xs text-gray-500 mt-1">win rate</p>
              </div>
              <TrendingUp className="h-10 w-10 text-purple-600" />
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg Deal</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">
                  €{(avgDealValue / 1000).toFixed(0)}k
                </p>
                <p className="text-xs text-gray-500 mt-1">per deal</p>
              </div>
              <Target className="h-10 w-10 text-blue-600" />
            </div>
          </Card>
        </div>

        {/* Charts - Only if data */}
        {stageChartData.length > 0 && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Sales Funnel by Stage</h3>
            <div style={{ width: '100%', height: 350 }}>
              <ResponsiveContainer>
                <BarChart data={stageChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="stage" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="left" orientation="left" allowDecimals={false} />
                  <YAxis yAxisId="right" orientation="right" allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="deals" fill="#1C3E53" name="Deals Count" />
                  <Bar yAxisId="right" dataKey="value" fill="#2D7A4C" name="Total Value (€)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        )}
        
        {/* Empty state */}
        {filteredTasks.length === 0 && (
          <Card className="p-12 text-center">
            <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Sales Data</h3>
            <p className="text-gray-600">Create deals to see sales analytics</p>
          </Card>
        )}
      </div>
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h2>
          <p className="text-sm text-gray-600 mt-1">{boardName} - Performance Insights</p>
        </div>
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="all">All time</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Render board-specific analytics */}
      {boardType === 'project' && renderProjectAnalytics()}
      {boardType === 'marketing' && renderMarketingAnalytics()}
      {boardType === 'sales' && renderSalesAnalytics()}
      {!boardType && renderProjectAnalytics()}
    </div>
  );
};

export default AnalyticsDashboard;
